import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, Star, Inbox, Package } from 'lucide-react';
import AppShell from '@/components/ops/AppShell';
import JobDetailModal from '@/components/JobDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const STATUS_TONE = {
  New:         'bg-blue-50 text-blue-700 border-blue-100',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-100',
  Active:      'bg-amber-50 text-amber-700 border-amber-100',
  Complete:    'bg-emerald-50 text-emerald-700 border-emerald-100',
  Recalled:    'bg-red-50 text-red-700 border-red-100',
  Pending:     'bg-slate-100 text-slate-600 border-slate-200',
};
const TASK_TONE = {
  not_started: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-100',
  blocked:     'bg-red-50 text-red-700 border-red-100',
  complete:    'bg-emerald-50 text-emerald-700 border-emerald-100',
};
const TASK_LABEL = { not_started: 'Not started', in_progress: 'In progress', blocked: 'Blocked', complete: 'Complete' };

function StatusPill({ status }) {
  return <span className={`ops-pill ${STATUS_TONE[status] || STATUS_TONE.Pending}`}>{status}</span>;
}
function SortIcon({ dir }) {
  if (dir === 'asc') return <ChevronUp size={11} className="text-slate-700" />;
  if (dir === 'desc') return <ChevronDown size={11} className="text-slate-700" />;
  return <ChevronsUpDown size={11} className="text-slate-400" />;
}
function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtRel(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < day) return 'Today';
  if (diff < day * 2) return 'Yesterday';
  if (diff < day * 7) return `${Math.floor(diff / day)}d ago`;
  if (diff < day * 30) return `${Math.floor(diff / (day * 7))}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function JobsTable({ jobs, onRowClick, searchTerm, setSearchTerm }) {
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const visible = useMemo(() => {
    let arr = jobs.slice();
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      arr = arr.filter(j =>
        `${j.client_first_name} ${j.client_last_name}`.toLowerCase().includes(s) ||
        (j.loved_one_first_name || '').toLowerCase().includes(s)
      );
    }
    const getter = {
      client:     j => `${j.client_first_name || ''} ${j.client_last_name || ''}`.toLowerCase(),
      loved_one:  j => (j.loved_one_first_name || '').toLowerCase(),
      progress:   j => j._total ? (j._done / j._total) : 0,
      status:     j => j.status || '',
      deadline:   j => j.overall_deadline || '',
      created_at: j => j.created_at || '',
    }[sortKey] || (j => j.created_at || '');
    arr.sort((a, b) => {
      const va = getter(a); const vb = getter(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [jobs, sortKey, sortDir, searchTerm]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by client or loved one…"
            data-testid="search-jobs"
            className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md pl-8 pr-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
          />
        </div>
        <span className="text-[12px] text-slate-500 ml-1" data-testid="jobs-count">{visible.length} result{visible.length === 1 ? '' : 's'}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {visible.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Package size={20} className="mx-auto text-slate-300 mb-2" />
            <p className="text-[14px] text-slate-700 font-medium mb-1">No jobs in this view</p>
            <p className="text-[12px] text-slate-500">Try clearing the search or switching tabs.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" data-testid="jobs-table">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                  {[
                    { key: 'client',     label: 'Client',         w: 'w-[22%]' },
                    { key: 'loved_one',  label: 'Loved one',      w: 'w-[14%]' },
                    { key: 'progress',   label: 'Progress',       w: 'w-[20%]' },
                    { key: 'status',     label: 'Status',         w: 'w-[12%]' },
                    { key: 'deadline',   label: 'Deadline',       w: 'w-[14%]' },
                    { key: 'created_at', label: 'Received',       w: 'w-[18%]' },
                  ].map(col => (
                    <th
                      key={col.key}
                      data-sortable="true"
                      data-testid={`jobs-sort-${col.key}`}
                      onClick={() => toggleSort(col.key)}
                      className={`${col.w} px-4 py-2.5`}
                    >
                      <span className="inline-flex items-center gap-1 hover:text-slate-900">
                        {col.label}
                        <SortIcon dir={sortKey === col.key ? sortDir : null} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-[13px] text-slate-700">
                {visible.map(job => {
                  const pct = job._total ? (job._done / job._total) * 100 : 0;
                  return (
                    <tr
                      key={job.id}
                      onClick={() => onRowClick(job)}
                      data-testid={`job-row-${job.id}`}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900 truncate">{job.client_first_name} {job.client_last_name}</div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700 truncate">{job.loved_one_first_name || '—'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-slate-500 whitespace-nowrap">{job._done}/{job._total}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5"><StatusPill status={job.status} /></td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(job.overall_deadline)}</td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtRel(job.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function MyTasksList({ tasks, onTaskClick }) {
  if (tasks.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-lg text-center py-16 px-6">
        <Inbox size={20} className="mx-auto text-slate-300 mb-2" />
        <p className="text-[14px] text-slate-700 font-medium mb-1">No active tasks assigned to you</p>
        <p className="text-[12px] text-slate-500">Tasks assigned by the Production Lead will appear here.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <ul className="divide-y divide-slate-100" data-testid="my-tasks-list">
        {tasks.map(t => (
          <li key={t.id}>
            <button
              onClick={() => onTaskClick(t)}
              data-testid={`my-task-${t.id}`}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-900 truncate">{t.name}</p>
                <p className="text-[12px] text-slate-500 truncate mt-0.5">{t.job_client_name} · {t.job_loved_one_first_name || '—'}</p>
              </div>
              <span className={`ops-pill ${TASK_TONE[t.status]}`}>{TASK_LABEL[t.status]}</span>
              {t.deadline && <span className="text-[12px] text-slate-500 whitespace-nowrap">Due {fmtDate(t.deadline)}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function OpsProduction() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    // Allow admin to view production page (admin has elevated access).
    if (!user.roles?.includes('production') && !user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [allJobsRes, usersRes, mineRes] = await Promise.all([
        axios.get(`${API}/ops/production-jobs?include=all`, getAuth()),
        axios.get(`${API}/ops/users`, getAuth()).catch(() => ({ data: [] })),
        axios.get(`${API}/ops/my-tasks`, getAuth()).catch(() => ({ data: [] })),
      ]);
      const enriched = await Promise.all(allJobsRes.data.map(async j => {
        try {
          const { data: ts } = await axios.get(`${API}/ops/production-jobs/${j.id}/tasks`, getAuth());
          return { ...j, _total: ts.length, _done: ts.filter(t => t.status === 'complete').length };
        } catch { return { ...j, _total: 0, _done: 0 }; }
      }));
      setJobs(enriched);
      setUsers(usersRes.data);
      setMyTasks(mineRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  if (authLoading || loading) {
    return (
      <div className="ops-shell min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const isAdmin = user?.roles?.includes('admin');
  const isProdLead = !!user?.is_production_lead;
  const activeJobs   = jobs.filter(j => j.status !== 'Recalled' && j.status !== 'Complete');
  const recalledJobs = jobs.filter(j => j.status === 'Recalled');
  const subtitle = `${activeJobs.length} active · ${myTasks.length} my task${myTasks.length === 1 ? '' : 's'} · ${recalledJobs.length} recalled`;

  let visibleJobs = jobs;
  if (tab === 'active')   visibleJobs = activeJobs;
  if (tab === 'recalled') visibleJobs = recalledJobs;

  const handleTaskClick = (task) => {
    const job = jobs.find(j => j.id === task.production_job_id);
    setSelectedTaskId(task.id);
    if (job) setSelectedJob(job);
    else axios.get(`${API}/ops/production-jobs/${task.production_job_id}`, getAuth()).then(r => setSelectedJob(r.data));
  };

  const topbarActions = isProdLead ? (
    <span className="ops-pill bg-amber-50 text-amber-700 border-amber-100" data-testid="prod-lead-badge">
      <Star size={10} /> Production Lead
    </span>
  ) : null;

  return (
    <AppShell
      pageTitle="Production"
      subtitle={subtitle}
      activeNav={isAdmin ? 'production' : 'jobs'}
      onNavigate={() => {}}
      role={isAdmin ? 'admin' : 'production'}
      topbarActions={topbarActions}
    >
      <div data-testid="ops-production-page" className="px-6 py-5">
        {/* Sub-tab chip strip */}
        <div className="flex items-center gap-1 mb-4" data-testid="production-tabs">
          {[
            { v: 'active',   l: 'Active jobs',  count: activeJobs.length },
            { v: 'mine',     l: 'My tasks',     count: myTasks.length },
            { v: 'recalled', l: 'Recalled',     count: recalledJobs.length },
            { v: 'all',      l: 'All jobs',     count: jobs.length },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`production-tab-${t.v}`}
              className={`text-[12px] px-3 py-1 rounded-md transition-colors ${tab === t.v ? 'bg-white border border-slate-200 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
            >
              {t.l} <span className="text-slate-400 ml-1">{t.count}</span>
            </button>
          ))}
        </div>

        {tab === 'mine' ? (
          <MyTasksList tasks={myTasks} onTaskClick={handleTaskClick} />
        ) : (
          <JobsTable
            jobs={visibleJobs}
            onRowClick={(j) => { setSelectedTaskId(null); setSelectedJob(j); }}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
      </div>

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          users={users}
          initialExpandedTaskId={selectedTaskId}
          onClose={() => { setSelectedJob(null); setSelectedTaskId(null); }}
          onUpdated={fetchData}
        />
      )}
    </AppShell>
  );
}
